import { useEffect, useRef, useState } from "react";
import { ChevronDown, Download, Film, FileText, FolderInput, Image as ImageIcon, Link2, ListChecks, Pause, Pencil, Play, Plus, RefreshCw, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { ActionModal, Button, EmptyState, Field, Icon, Picker, Spinner } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useCapability } from "../../../app/bandContext.js";
import { useRelease, useUpdateAsset, useDeleteAsset, useSyncTracksFromSpotify, useSyncDspLinks } from "../../../app/hooks/useRelease.js";
import { dspSyncMessage } from "../dspSyncMessage.js";
import { useMediaObjectUrl, useMediaDownload } from "../../../app/hooks/useMedia.js";
import { errorMessage } from "../../../app/hooks/apiError.js";
import { formatFileSize } from "../../../app/hooks/inboxFormat.js";
import { pushToast } from "../../../app/toasts.js";
import { SectionRail } from "../sectionRail.js";
import { MediaImage } from "../MediaImage.js";
import { AssetEditorModal, type AssetKind } from "../AssetEditorModal.js";
import { SongEditorModal } from "../SongEditorModal.js";
import { ImportFilesModal } from "../ImportFilesModal.js";
import { TaskDrawer } from "../../tasks/TaskDrawer.js";
import styles from "../section.module.scss";
import own from "./AssetsSection.module.scss";

type AssetDto = components["schemas"]["AssetDto"];
type TrackDto = components["schemas"]["TrackDto"];
type SpotifyTrackSyncResult = components["schemas"]["SpotifyTrackSyncResult"];
type UnmatchedTrackDto = components["schemas"]["UnmatchedTrackDto"];

// Assets — the Asset Library (shot-10). The full library over the media/asset API: the 4
// canonical tiles (cover/lyrics/music-video/lyric-video) + a Songs list (the release tracks) + a Photos gallery
// + the Spotify Canvas uploader, with the segmented progress bar (server-DERIVED, never stored). Uploads run the
// presigned flow with visible progress; a failed upload surfaces and never reports success. The
// asset editor reuses the release Modal primitive. Writes are gated releases:write; readers see it read-only.

const PHOTO_ROLES = ["Profile", "Press", "Live", "Promo", "Behind the scenes", "Other"];
const PHOTO_CAP = 20;

// Spotify Canvas sits UP TOP with the other canonical assets (Ryan, ART-201), not in its own row below.
const CANONICAL_ORDER: AssetKind[] = ["cover", "lyrics", "music_video", "lyric_video", "canvas"];

interface CanonicalDef {
  label: string;
  icon: LucideIcon;
  sub: (a: AssetDto | undefined) => string;
}

const CANONICAL: Record<string, CanonicalDef> = {
  cover: { label: "Cover artwork", icon: ImageIcon, sub: (a) => (a?.status === "added" ? "Final artwork uploaded" : "No cover yet") },
  lyrics: { label: "Lyrics", icon: FileText, sub: (a) => (a?.textContent ? `${lineCount(a.textContent)} lines` : "No lyrics yet") },
  music_video: { label: "Music video", icon: Link2, sub: (a) => a?.url || "No link yet" },
  lyric_video: { label: "Lyric video", icon: Link2, sub: (a) => a?.url || "No link yet" },
  canvas: { label: "Spotify Canvas", icon: Film, sub: (a) => (a?.status === "added" ? "9:16 loop uploaded" : "9:16 loop, 3–8s") },
};

export function AssetsSection({ releaseId }: { releaseId: string }) {
  const canWrite = useCapability("releases:write");
  const q = useRelease(releaseId);
  const cc = q.data?.data;
  const version = q.data?.version;

  const [editor, setEditor] = useState<{ kind?: AssetKind; asset?: AssetDto } | null>(null);
  // null = closed; {} = add a new song; { track } = edit that song (attach/replace its master, rename, DELETE).
  const [songEditor, setSongEditor] = useState<{ track?: TrackDto } | null>(null);
  // ART-368: a slot filled from a task's media opens that task.
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  // ART-369: the migration import — files from the person's own Dropbox into this release.
  const [importing, setImporting] = useState(false);

  if (q.isLoading || !cc?.assets) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={20} label="Loading assets…" />
      </div>
    );
  }

  const items = cc.assets.items ?? [];
  const byKind = (k: string) => items.find((a) => a.kind === k);
  const photos = items.filter((a) => a.kind === "photo");
  const files = items.filter((a) => a.kind === "file");
  const tracks = cc.tracks ?? [];
  const progress = cc.assets.progress ?? {};
  const total = progress.total ?? 0;
  const added = progress.added ?? 0;

  return (
    <div className={styles.section}>
      <SectionRail>
        {canWrite ? (
          <>
            <Button size="sm" onClick={() => setEditor({})}>
              <Icon icon={Plus} size={16} /> Add asset
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setImporting(true)}>
              <Icon icon={FolderInput} size={16} /> Import
            </Button>
            {/* Keyed on the Spotify ALBUM ID alone. The server can also fall back to the smart link's Spotify
                button, but bands point that at search pages and pre-saves, so it would mostly be a press whose
                only answer is "add the album ID first" — without one the button is disabled and says so. */}
            <SpotifySyncButton releaseId={releaseId} hasSongs={tracks.length > 0} hasAlbum={Boolean(cc.overview?.spotifyAlbumId)} />
          </>
        ) : null}
      </SectionRail>

      {/* The deliverables progress moved out of the (now removed) section header into the body (ART-201). */}
      {total > 0 && <p className={styles.eyebrow}>{added} / {total} in</p>}

      <div className={own.topGrid}>
        <div className={own.tileGrid}>
          {CANONICAL_ORDER.map((kind) => (
            <CanonicalTile
              key={kind}
              kind={kind}
              asset={byKind(kind)}
              seed={releaseId}
              canWrite={canWrite}
              onEdit={(k, a) => setEditor({ kind: k, asset: a })}
              onOpenTask={setOpenTaskId}
            />
          ))}
        </div>
        <SongsCard
          tracks={tracks}
          canWrite={canWrite}
          onAddSong={() => setSongEditor({})}
          onEditSong={(t) => setSongEditor({ track: t })}
        />
      </div>

      <FilesCard files={files} canWrite={canWrite} onEdit={(a) => setEditor({ kind: "file", asset: a })} onAdd={() => setEditor({ kind: "file" })} onOpenTask={setOpenTaskId} />

      <PhotosCard photos={photos} releaseId={releaseId} seed={releaseId} canWrite={canWrite} onAdd={() => setEditor({ kind: "photo" })} />

      <ProgressBar progress={progress} />

      {editor && (
        <AssetEditorModal releaseId={releaseId} seed={releaseId} kind={editor.kind} asset={editor.asset} onClose={() => setEditor(null)} />
      )}
      {openTaskId && <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} />}
      {importing && <ImportFilesModal releaseId={releaseId} onClose={() => setImporting(false)} />}
      {songEditor && version !== undefined && (
        <SongEditorModal releaseId={releaseId} version={version} existingTracks={tracks} editTrack={songEditor.track} onClose={() => setSongEditor(null)} />
      )}
    </div>
  );
}

// ---- canonical tiles (cover / lyrics / music-video / lyric-video) --------------------------------------

function CanonicalTile({
  kind,
  asset,
  seed,
  canWrite,
  onEdit,
  onOpenTask,
}: {
  kind: AssetKind;
  asset: AssetDto | undefined;
  seed: string;
  canWrite: boolean;
  onEdit: (kind: AssetKind, asset: AssetDto | undefined) => void;
  onOpenTask: (taskId: string) => void;
}) {
  const def = CANONICAL[kind];
  const { download } = useMediaDownload();
  const status = asset?.status ?? "missing";

  const content = (
    <>
      <span className={own.thumb} aria-hidden="true">
        {kind === "cover" && asset?.mediaId ? (
          <MediaImage mediaId={asset.mediaId} url={asset.mediaUrl} seed={seed} alt="" className={own.thumbImg} />
        ) : (
          <Icon icon={def.icon} size={20} />
        )}
      </span>
      <span className={own.tileText}>
        <span className={own.tileKind}>{def.label}</span>
        <span className={own.tileSub}>{fromTask(asset) ?? def.sub(asset)}</span>
      </span>
    </>
  );

  return (
    <div className={own.tile} data-state={status}>
      {canWrite ? (
        <button type="button" className={own.tileMain} onClick={() => onEdit(kind, asset)}>
          {content}
        </button>
      ) : (
        <div className={own.tileMain}>{content}</div>
      )}
      <span className={own.tileEnd}>
        <TaskSourceButton asset={asset} onOpenTask={onOpenTask} />
        <StatePill kind={kind} status={status} />
        {kind === "cover" && asset?.mediaId && (
          <button
            type="button"
            className={own.iconBtn}
            aria-label="Download cover"
            onClick={() => download(asset.mediaId!, `cover-${asset.assetId}`, asset.mediaUrl).catch((e) => pushToast(errorMessage(e, "Couldn't download the cover")))}
          >
            <Icon icon={Download} size={16} />
          </button>
        )}
      </span>
    </div>
  );
}

// ---- Songs (the release tracks + masters) --------------------------------------------------------------

function SongsCard({
  tracks, canWrite, onAddSong, onEditSong,
}: {
  tracks: TrackDto[];
  canWrite: boolean;
  onAddSong: () => void;
  onEditSong: (t: TrackDto) => void;
}) {
  return (
    <div className={own.songs}>
      <div className={own.songsHead}>
        <h3 className={own.songsTitle}>Tracks</h3>
        {canWrite && (
          <Button variant="ghost" size="sm" onClick={onAddSong}>
            <Icon icon={Plus} size={16} /> Add song
          </Button>
        )}
      </div>
      {tracks.length === 0 ? (
        <p className={own.songsEmpty}>No songs yet.</p>
      ) : (
        <ul className={own.songList}>
          {tracks.map((t) => (
            <SongRow key={t.trackId} track={t} canWrite={canWrite} onEdit={() => onEditSong(t)} />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * ART-219 — in the Assets action rail, under "Add asset": "Fetch from Spotify" on an empty song list, "Sync tracks"
 * once there are songs — one action, named for what it will do — and, disabled, "No Spotify ID" when the release
 * has no album ID to fetch from. Songs that match the Spotify album are kept and missing ones added. If any song matches
 * NOTHING on the album, the first press writes nothing and the server names those songs; the person then decides
 * whether to keep or remove them, and the second press carries that answer.
 */
function SpotifySyncButton({ releaseId, hasSongs, hasAlbum }: { releaseId: string; hasSongs: boolean; hasAlbum: boolean }) {
  const sync = useSyncTracksFromSpotify(releaseId);
  const syncLinks = useSyncDspLinks(releaseId, { silent: true });
  const [unmatched, setUnmatched] = useState<UnmatchedTrackDto[] | null>(null);
  const [answering, setAnswering] = useState<"keep" | "remove" | null>(null);

  async function run(removeUnmatched: boolean | null) {
    try {
      const result = await sync.mutateAsync({ removeUnmatched });
      if (result.status === "needs-decision") {
        setUnmatched(result.unmatched ?? []);
        return;
      }
      setUnmatched(null);
      const [message, tone] = syncMessage(result);
      pushToast(message, tone);
      // Follow-up: new Spotify ids are exactly what the DSP link lookup keys on, so run it for this release now
      // rather than leave the smart link empty until the hourly job. Quiet unless it found something — a miss, an
      // outage or the once-a-minute limit is not worth a second toast over a sync that worked.
      if (result.status === "synced" && (result.added ?? 0) + (result.backfilled ?? 0) > 0) {
        void syncLinks
          .mutateAsync()
          .then((links) => {
            if (links.status === "found") {
              const [m, t] = dspSyncMessage(links);
              pushToast(m, t);
            }
          })
          .catch(() => undefined);
      }
    } catch {
      // The hook's onError already toasted; close the prompt so a failed answer can be retried from the button.
      setUnmatched(null);
    } finally {
      setAnswering(null);
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => void run(null)}
        disabled={!hasAlbum || sync.isPending}
        aria-busy={sync.isPending || undefined}
        title={hasAlbum ? undefined : "Add the Spotify album ID in Overview"}
      >
        <Icon icon={RefreshCw} size={16} /> {!hasAlbum ? "No Spotify ID" : hasSongs ? "Sync tracks" : "Fetch from Spotify"}
      </Button>
      {unmatched && (
        <ActionModal
          title="Songs not on the Spotify album"
          onCancel={() => setUnmatched(null)}
          actions={[
            { label: "Keep them", loading: answering === "keep", onClick: () => { setAnswering("keep"); void run(false); } },
            { label: "Remove them", tone: "danger", loading: answering === "remove", onClick: () => { setAnswering("remove"); void run(true); } },
          ]}
        >
          <p>
            {unmatched.length === 1 ? "This song doesn't" : `These ${unmatched.length} songs don't`} match anything on
            the release's Spotify album. Songs that do match are kept, and missing ones will be added either way.
          </p>
          <ul className={own.syncUnmatched}>
            {unmatched.map((u) => (
              <li key={u.trackId}>{u.title}</li>
            ))}
          </ul>
          <p className={own.syncNote}>Removing a song also removes its audio and any versions attached to it.</p>
        </ActionModal>
      )}
    </>
  );
}

/** One sentence for what a press came to. Every status but `synced` is an answer about why nothing changed. */
function syncMessage(r: SpotifyTrackSyncResult): [string, "success" | "info" | "warning" | "error"] {
  switch (r.status) {
    case "synced": {
      const parts: string[] = [];
      if (r.added) parts.push(`added ${r.added} ${plural(r.added, "song")}`);
      if (r.backfilled) parts.push(`linked ${r.backfilled} existing ${plural(r.backfilled, "song")}`);
      if (r.removed) parts.push(`removed ${r.removed}`);
      const kept = r.unmatched?.length ?? 0;
      if (kept) parts.push(`kept ${kept} not on the album`);
      if (parts.length === 0) return ["Songs already match Spotify.", "info"];
      const text = parts.join(", ");
      return [`${text.charAt(0).toUpperCase()}${text.slice(1)}.`, "success"];
    }
    case "no-album":
      return ["Add the release's Spotify album ID in Overview first.", "warning"];
    case "not-found":
      return ["Spotify couldn't find that album. Check the album ID in Overview.", "warning"];
    case "not-configured":
      return ["Spotify isn't set up on this server yet.", "warning"];
    default:
      return ["Couldn't reach Spotify. Try again in a moment.", "error"];
  }
}

function plural(n: number, word: string) {
  return n === 1 ? word : `${word}s`;
}

function SongRow({ track, canWrite, onEdit }: { track: TrackDto; canWrite: boolean; onEdit: () => void }) {
  const [playing, setPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { download } = useMediaDownload();
  // Lazy: the master (potentially tens of MB) is only fetched when the row is actually played.
  const master = useMediaObjectUrl(playing ? track.masterMediaId : null);
  const hasMaster = Boolean(track.masterMediaId);
  const variants = track.variants ?? [];
  // The master's sub-line: its uploaded filename + size (the same join the variants show). masterFileName is
  // null when the media row is absent (redacted) though masterMediaId is set — fall back to a plain label.
  const masterSub = hasMaster
    ? [track.masterFileName ?? "Master file", track.masterSizeBytes ? formatFileSize(track.masterSizeBytes) : null].filter(Boolean).join(" · ")
    : "No master file";

  // A failed lazy master fetch would otherwise leave the button stuck on Pause with no audio + no feedback —
  // surface it and reset the toggle so the control matches reality (no silently-swallowed playback error).
  useEffect(() => {
    if (playing && master.error) {
      setPlaying(false);
      pushToast("Couldn't load the master audio.");
    }
  }, [playing, master.error]);

  return (
    <li className={own.song}>
      <button
        type="button"
        className={own.songPlay}
        disabled={!hasMaster}
        aria-label={playing ? "Stop" : "Play master"}
        onClick={() => setPlaying((p) => !p)}
      >
        <Icon icon={playing ? Pause : Play} size={16} variant="filled" />
      </button>
      <div className={own.songText}>
        <span className={own.songTitleRow}>
          <span className={own.songTitle}>{track.title || `Track ${track.position ?? ""}`}</span>
          {variants.length > 0 && (
            <button
              type="button"
              className={own.songExpander}
              data-expanded={expanded || undefined}
              aria-expanded={expanded}
              aria-label={expanded ? "Hide versions" : `Show ${variants.length} version${variants.length === 1 ? "" : "s"}`}
              onClick={() => setExpanded((e) => !e)}
            >
              <Icon icon={ChevronDown} size={16} />
            </button>
          )}
        </span>
        <span className={own.songSub}>{masterSub}</span>
      </div>
      {(hasMaster || canWrite) && (
        <div className={own.songActions}>
          {hasMaster && (
            <button
              type="button"
              className={own.iconBtn}
              aria-label="Download master"
              onClick={() => download(track.masterMediaId!, `${track.title || "master"}`).catch((e) => pushToast(errorMessage(e, "Couldn't download the master")))}
            >
              <Icon icon={Download} size={16} />
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              className={own.iconBtn}
              aria-label={hasMaster ? "Replace master" : "Add master"}
              onClick={onEdit}
            >
              <Icon icon={Pencil} size={16} />
            </button>
          )}
        </div>
      )}
      {playing && master.url && (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- a music master has no captions track
        <audio className={own.audio} src={master.url} controls autoPlay onEnded={() => setPlaying(false)} />
      )}
      {variants.length > 0 && expanded && (
        <div className={own.songVariants}>
          {variants.map((v) => (
            <div key={v.variantId} className={own.variantRow}>
              <span className={own.variantChip}>{v.label}</span>
              <span className={own.variantMeta}>
                <span className={own.variantName}>{v.fileName ?? "File"}</span>
                {v.sizeBytes ? <span className={own.variantSize}>· {formatFileSize(v.sizeBytes)}</span> : null}
              </span>
              <div className={own.variantActions}>
                <button
                  type="button"
                  className={own.iconBtn}
                  aria-label={`Download ${v.label}`}
                  onClick={() => download(v.mediaId!, `${track.title || "track"} — ${v.label}`, v.mediaUrl).catch((e) => pushToast(errorMessage(e, "Couldn't download the version")))}
                >
                  <Icon icon={Download} size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </li>
  );
}

// ---- Photos gallery ------------------------------------------------------------------------------------

function PhotosCard({
  photos,
  releaseId,
  seed,
  canWrite,
  onAdd,
}: {
  photos: AssetDto[];
  releaseId: string;
  seed: string;
  canWrite: boolean;
  onAdd: () => void;
}) {
  return (
    <div className={styles.card}>
      <div className={own.photosHead}>
        <h3 className={styles.cardTitle}>
          Photos <span className={own.count}>{photos.length} / {PHOTO_CAP}</span>
        </h3>
        {canWrite && (
          <Button variant="ghost" size="sm" onClick={onAdd} disabled={photos.length >= PHOTO_CAP}>
            <Icon icon={Plus} size={16} /> Add photos
          </Button>
        )}
      </div>
      {photos.length === 0 ? (
        <EmptyState title="No photos yet" description="Add press, live, and promo photos with a role and caption." />
      ) : (
        <div className={own.photoGrid}>
          {photos.map((p) => (
            <PhotoRow key={p.assetId} releaseId={releaseId} photo={p} seed={seed} canWrite={canWrite} />
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoRow({ releaseId, photo, seed, canWrite }: { releaseId: string; photo: AssetDto; seed: string; canWrite: boolean }) {
  const update = useUpdateAsset(releaseId);
  const remove = useDeleteAsset(releaseId);
  const { download } = useMediaDownload();
  const [role, setRole] = useState(photo.photoRole ?? PHOTO_ROLES[0]);
  const [caption, setCaption] = useState(photo.caption ?? "");
  // A role edit persists immediately, which refetches the composed record mid-caption-edit. Guard the caption
  // resync while the field is focused so we never overwrite the keystrokes the user is still typing; and compare
  // blur commits against the LAST value this row persisted/synced (not the possibly-stale `photo.caption`) so a
  // role-change-then-blur doesn't fire a second, redundant PUT.
  const captionFocused = useRef(false);
  const lastCaption = useRef(photo.caption ?? "");

  useEffect(() => {
    setRole(photo.photoRole ?? PHOTO_ROLES[0]);
    lastCaption.current = photo.caption ?? "";
    if (!captionFocused.current) setCaption(photo.caption ?? "");
  }, [photo.photoRole, photo.caption]);

  function persist(nextRole: string, nextCaption: string) {
    lastCaption.current = nextCaption.trim();
    update.mutate({
      assetId: photo.assetId!,
      body: {
        kind: "photo",
        status: photo.status ?? "added",
        mediaId: photo.mediaId ?? null,
        url: null,
        textContent: null,
        photoRole: nextRole,
        caption: nextCaption.trim() || null,
        position: photo.position ?? null,
      },
    });
  }

  return (
    <div className={own.photo}>
      <MediaImage mediaId={photo.mediaId} url={photo.mediaUrl} seed={seed} alt={photo.caption || "Release photo"} className={own.photoThumb} />
      <div className={own.photoMeta}>
        {canWrite ? (
          <>
            <Picker
              label="Photo role"
              hideLabel
              value={role}
              onChange={(next) => {
                setRole(next);
                persist(next, caption);
              }}
              options={PHOTO_ROLES.map((r) => ({ value: r, label: r }))}
            />
            <Field
              label="Photo caption"
              hideLabel
              value={caption}
              placeholder="Caption"
              onFocus={() => {
                captionFocused.current = true;
              }}
              onChange={(e) => setCaption(e.target.value)}
              onBlur={() => {
                captionFocused.current = false;
                if (caption.trim() !== lastCaption.current) persist(role, caption);
              }}
            />
            <span className={own.photoActions}>
              {photo.mediaId && (
                <button type="button" className={own.iconBtn} aria-label="Download photo" onClick={() => download(photo.mediaId!, `photo-${photo.assetId}`, photo.mediaUrl).catch((e) => pushToast(errorMessage(e, "Couldn't download the photo")))}>
                  <Icon icon={Download} size={16} />
                </button>
              )}
              <button type="button" className={own.iconBtn} aria-label="Remove photo" onClick={() => remove.mutate(photo.assetId!)} disabled={remove.isPending}>
                <Icon icon={X} size={16} />
              </button>
            </span>
          </>
        ) : (
          <div className={own.photoRead}>
            <span className={own.photoRole}>{photo.photoRole || "Photo"}</span>
            {photo.caption && <span className={own.photoCaption}>{photo.caption}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Files (the generic Label/Asset combos, ART-201) ---------------------------------------------------

// The open-ended asset library: any labelled file (stems, split sheets, alt art, contracts…) with NO preset
// category — just a Label + the file (Ryan: "assets don't have preset categories … they're just Label / Asset
// combos"). The structured deliverables (cover/lyrics/videos/canvas/photo/audio) keep their own tiles above;
// this is everything else. A row opens the same editor to relabel or replace; the header "+ Add file" and the
// section's top-right "+ Add asset" both open it fresh.
function FilesCard({
  files,
  canWrite,
  onEdit,
  onAdd,
  onOpenTask,
}: {
  files: AssetDto[];
  canWrite: boolean;
  onEdit: (asset: AssetDto) => void;
  onAdd: () => void;
  onOpenTask: (taskId: string) => void;
}) {
  const { download } = useMediaDownload();

  return (
    <div className={styles.card}>
      <div className={own.photosHead}>
        <h3 className={styles.cardTitle}>
          Files {files.length > 0 && <span className={own.count}>{files.length}</span>}
        </h3>
        {canWrite && (
          <Button variant="ghost" size="sm" onClick={onAdd}>
            <Icon icon={Plus} size={16} /> Add file
          </Button>
        )}
      </div>
      {files.length === 0 ? (
        <EmptyState title="No files yet" description="Add any labelled file — stems, a split sheet, alternate art." />
      ) : (
        <ul className={own.fileList}>
          {files.map((f) => {
            const name = f.label?.trim() || "Untitled file";
            const row = (
              <>
                <span className={own.fileIcon} aria-hidden="true"><Icon icon={FileText} size={16} /></span>
                <span className={own.fileText}>
                  <span className={own.fileLabel}>{name}</span>
                  <span className={own.fileSub}>{fromTask(f) ?? (f.mediaId ? "File attached" : "No file")}</span>
                </span>
              </>
            );
            return (
              <li key={f.assetId} className={own.file}>
                {canWrite ? (
                  <button type="button" className={own.fileMain} onClick={() => onEdit(f)}>{row}</button>
                ) : (
                  <div className={own.fileMain}>{row}</div>
                )}
                <TaskSourceButton asset={f} onOpenTask={onOpenTask} />
                {f.mediaId && (
                  <button
                    type="button"
                    className={own.iconBtn}
                    aria-label={`Download ${name}`}
                    onClick={() => download(f.mediaId!, name, f.mediaUrl).catch((e) => pushToast(errorMessage(e, "Couldn't download the file")))}
                  >
                    <Icon icon={Download} size={16} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---- segmented progress bar (server-derived; never stored) ---------------------------------------------

function ProgressBar({ progress }: { progress: components["schemas"]["AssetProgress"] }) {
  const total = progress.total ?? 0;
  const added = progress.added ?? 0;
  const inProgress = progress.inProgress ?? 0;
  const missing = progress.missing ?? 0;
  const pct = (n: number) => (total > 0 ? `${(n / total) * 100}%` : "0%");

  return (
    <div className={own.progressCard}>
      <div className={own.segTrack} role="img" aria-label={`${added} in, ${inProgress} in progress, ${missing} missing`}>
        <span className={own.segAdded} style={{ width: pct(added) }} />
        <span className={own.segProgress} style={{ width: pct(inProgress) }} />
        <span className={own.segMissing} style={{ width: pct(missing) }} />
      </div>
      <div className={own.legend}>
        <LegendDot cls={own.dotAdded} label={`${added} in`} />
        <LegendDot cls={own.dotProgress} label={`${inProgress} WIP`} />
        <LegendDot cls={own.dotMissing} label={`${missing} missing`} />
      </div>
    </div>
  );
}

// ---- shared bits ---------------------------------------------------------------------------------------

/** "From task · Album Art Designs" — a slot filled by a push from a task's media (ART-368) — or "From Dropbox" for
 *  one the migration import filled (ART-369). Null when the asset was added here, or when the reader cannot see
 *  that task (the server withholds its title). */
function fromTask(a: AssetDto | undefined): string | null {
  if (a?.sourceTaskId && a.sourceTaskTitle) return `From task · ${a.sourceTaskTitle}`;
  // ART-369: a slot filled by the migration import says where the file came from, until someone replaces it.
  if (a?.importedFrom) return `From ${IMPORT_SOURCE_NAMES[a.importedFrom] ?? "storage"}`;
  return null;
}

/** What each storage source is called on a tile ("From Dropbox"). */
const IMPORT_SOURCE_NAMES: Record<string, string> = { dropbox: "Dropbox" };

/** Opens the task a slot was pushed from. A sibling of the tile's own button, not inside it: a button cannot
 *  hold another. */
function TaskSourceButton({ asset, onOpenTask }: { asset: AssetDto | undefined; onOpenTask: (taskId: string) => void }) {
  // Only a TASK source opens anything; an imported file's "From Dropbox" is a fact, not a door.
  if (!asset?.sourceTaskId || !asset.sourceTaskTitle) return null;
  return (
    <button
      type="button"
      className={own.iconBtn}
      aria-label={`Open task ${asset?.sourceTaskTitle ?? ""}`.trim()}
      title="Open task"
      onClick={() => onOpenTask(asset!.sourceTaskId as string)}
    >
      <Icon icon={ListChecks} size={16} />
    </button>
  );
}

function StatePill({ kind, status }: { kind: string; status: string }) {
  const tone = status === "added" ? "added" : status === "in_progress" ? "progress" : "missing";
  const label =
    status === "added"
      ? kind === "cover"
        ? "In"
        : "Added"
      : status === "in_progress"
        ? "In progress"
        : kind === "music_video" || kind === "lyric_video"
          ? "No link"
          : "Missing";
  return (
    <span className={own.pill} data-tone={tone}>
      {label}
    </span>
  );
}

function LegendDot({ cls, label }: { cls: string; label: string }) {
  return (
    <span className={own.legendItem}>
      <span className={`${own.dot} ${cls}`} aria-hidden="true" /> {label}
    </span>
  );
}

function lineCount(text: string): number {
  return text.split("\n").filter((l) => l.trim().length > 0).length;
}
